/**
 * BoardLegend — the floating line at the bottom-left of a board that says what its colours mean.
 *
 * Shared by the honeycomb and the camera map, which draw the same two things and used to carry
 * two hand-written copies of the same row.
 *
 * WHAT IT HAS TO EXPLAIN, and why it is two groups and not one row:
 * a marker shows the camera's own state as two small dots (left = streaming, right = recording),
 * and, when an alert is live, the WHOLE marker pulses in that alert's severity colour. Those are
 * unrelated readings that happen to be told in the same visual language. Listing all five
 * swatches in one line under a sentence about the two dots left the red/amber/blue ones as three
 * colours with no stated meaning at all — so each group now carries its own label, and the
 * severity group says that what pulses is the marker.
 */
import { CAMERA_DOT_LEGEND, SEVERITY_LEGEND, type LegendSwatch } from './types';

function Swatch({ item }: { item: LegendSwatch }) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${item.c} ${item.pulse ? 'animate-pulse' : ''}`} />
      {item.label}
    </span>
  );
}

interface BoardLegendProps {
  /** What one marker is called on this board — "hexagon" or "pin". */
  marker: string;
  className?: string;
}

export default function BoardLegend({ marker, className = '' }: BoardLegendProps) {
  return (
    <div
      data-tour="alert-board-legend"
      /* items-center with gap-y: on a narrow viewport the two groups wrap onto separate lines
         rather than compressing, which is the one thing that must not happen to a legend. */
      className={`absolute bottom-3 left-3 z-40 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-card px-3 py-1.5 shadow-card ${className}`}
    >
      <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="text-[10px] font-semibold text-foreground">
          Each {marker} — left dot: Live, right dot: Recording
        </span>
        {CAMERA_DOT_LEGEND.map((item) => (
          <Swatch key={item.label} item={item} />
        ))}
      </span>

      <span className="h-3 w-px bg-border" aria-hidden />

      <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="text-[10px] font-semibold text-foreground">
          Alert severity — the {marker} pulses
        </span>
        {SEVERITY_LEGEND.map((item) => (
          <Swatch key={item.label} item={item} />
        ))}
      </span>
    </div>
  );
}
